import { Fragment, JSX } from 'react';
import { ArrayPath, Control, FieldArray, FieldValues, Path, useFieldArray } from 'react-hook-form';

import { Button, ButtonSizes, ButtonVariant, FormTextInput, TextInputProps, Typography } from '@/shared/ui';

import './MultipleFormTextInputFields.scss';

type InputPropsType = Pick<TextInputProps, 'name' | 'placeholder' | 'label' | 'required'>;

export interface MultipleFormTextInputFieldsProps<T extends FieldValues> {
  control: Control<T>;
  fieldName: ArrayPath<T>;
  inputsProps: InputPropsType[];
  addItemLabel: string;
  removeItemLabel: string;
  maxItems?: number;
}

export function MultipleFormTextInputFields<T extends FieldValues = FieldValues>({
  control,
  fieldName,
  inputsProps,
  maxItems = 5,
  addItemLabel,
  removeItemLabel,
}: MultipleFormTextInputFieldsProps<T>): JSX.Element {
  const { fields, append, remove } = useFieldArray({
    control,
    name: fieldName,
  });

  const showAddButton = fields.length < maxItems;
  const showRemoveButton = fields.length > 1;

  return (
    <div className="multiple-form-text-input-fields">
      {fields.map((field, itemIndex: number) => (
        <Fragment key={field.id}>
          <div className="multiple-form-text-input-fields__item">
            {inputsProps.map((inputProps: InputPropsType) => {
              const arrayFieldName: string & Path<T> = `${fieldName}.${itemIndex}.${inputProps.name}` as string &
                Path<T>;
              return (
                <FormTextInput<T>
                  control={control}
                  key={arrayFieldName}
                  name={arrayFieldName}
                  label={inputProps.label}
                  placeholder={inputProps.placeholder}
                  required={inputProps.required}
                />
              );
            })}
          </div>
          <div className="multiple-form-text-input-fields__buttons">
            {showAddButton && itemIndex === fields.length - 1 && (
              <Button
                variant={ButtonVariant.SECONDARY}
                size={ButtonSizes.SM}
                className="multiple-form-text-input-fields__add-button"
                onClick={() => append({} as FieldArray<T, ArrayPath<T>>)}
              >
                <Typography>{addItemLabel}</Typography>
              </Button>
            )}
            {showRemoveButton && (
              <Button
                variant={ButtonVariant.WARNING}
                size={ButtonSizes.SM}
                className="multiple-form-text-input-fields__delete-button"
                onClick={() => remove(itemIndex)}
              >
                <Typography>{removeItemLabel}</Typography>
              </Button>
            )}
          </div>
        </Fragment>
      ))}
    </div>
  );
}
